import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Calendar, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";

interface SuccessStepProps {
  firstName: string;
  calLink?: string;
}

const SuccessStep = ({ firstName, calLink }: SuccessStepProps) => {
  const { getSetting } = useBusinessSettings();
  const email = getSetting("email");
  useEffect(() => {
    const colors = ['#c9a8a8', '#e8d5d5', '#f5ebe0', '#d4a574'];
    
    // Heart shape function
    const heart = confetti.shapeFromPath({
      path: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'
    });

    // Gentle heart burst from center
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.5 },
      colors: colors,
      shapes: [heart],
      scalar: 1.5
    });

    // Delayed secondary burst
    setTimeout(() => {
      confetti({
        particleCount: 30,
        spread: 100,
        origin: { y: 0.6 },
        colors: colors,
        shapes: [heart],
        scalar: 1.2
      });
    }, 300);
  }, []);
  const handleScheduleClick = () => {
    if (calLink) {
      window.open(calLink, '_blank');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center text-center py-8"
    >
      {/* Success Animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
        className="relative mb-8"
      >
        <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
          <CheckCircle2 className="w-16 h-16 text-primary" />
        </div>
        
        {/* Sparkle decorations */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Sparkles className="absolute -top-3 -right-3 w-8 h-8 text-primary" />
          <Sparkles className="absolute -bottom-2 -left-4 w-6 h-6 text-primary/70" />
          <Sparkles className="absolute top-1/2 -right-6 w-5 h-5 text-primary/50" />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="max-w-md"
      >
        <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-3">
          You're all set{firstName ? `, ${firstName}` : ""}!
        </h2>
        <p className="text-lg text-muted-foreground mb-2">
          Your personalized brochure is on its way to your inbox.
        </p>
        <p className="text-muted-foreground mb-8">
          We can't wait to learn more about your wedding vision!
        </p>
      </motion.div>

      {/* Cal.com CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="w-full max-w-md space-y-4"
      >
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6">
          <Calendar className="w-10 h-10 text-primary mx-auto mb-4" />
          <h3 className="font-serif text-xl text-foreground mb-2">
            Ready to chat?
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            Schedule a free consultation call with our team to discuss your wedding day.
          </p>
          
          {calLink ? (
            <Button 
              onClick={handleScheduleClick}
              size="lg" 
              className="w-full"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule a Call
            </Button>
          ) : (
            <Button 
              size="lg" 
              className="w-full"
              asChild
            >
              <a href={`mailto:${email}?subject=Wedding Consultation Request`}>
                <Calendar className="w-4 h-4 mr-2" />
                Request a Call
              </a>
            </Button>
          )}
        </div>

        <div className="pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground mb-3">
            While you wait, explore our work:
          </p>
          <Button variant="outline" asChild className="w-full">
            <Link to="/portfolio">
              View Our Portfolio
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SuccessStep;
